// API service for hostbento onboarding
// The API endpoints are scoped to hostbento app path.
import type { OnboardingTask } from "@/types/onboard";
import { ApiService } from "./base";

interface OnboardingCreateRequest {
  business_name: string;
  description: string;
  industry: string;
  target_customers: string;
  personalities: string[];
  use_custom_colors: boolean;
  primary_color?: string;
  secondary_color?: string;
}

interface OnboardingCreateResponse {
  task_id: string;
  site_id: string;
  status_url: string;
  mcp_server_url: string;
}

interface RegenerateRequest {
  feedback: string;
  steps?: string[];
}

interface RegenerateResponse {
  task_id: string;
  regenerated_steps: string[];
  status_url: string;
}

interface CreateProjectRequest {
  project_name: string;
  project_purpose?: string;
}

interface CreateProjectResponse {
  task_id: string;
  site_id: string;
  project_name: string;
  project_id: string;
  mcp_server_url: string;
  site_url: string;
}

interface VerifyConnectionRequest {
  project_id: string;
}

interface VerifyConnectionResponse {
  connected: boolean;
  message: string;
}

export class HostbentoOnboardingApi extends ApiService {
  private readonly BASE_URL = "/api/v1/onboard";

  async create(data: OnboardingCreateRequest) {
    return this.request<OnboardingCreateResponse>(this.BASE_URL, {
      method: "POST",
      body: data,
    });
  }

  async getStatus(taskId: string) {
    return this.request<OnboardingTask>(`${this.BASE_URL}/${taskId}/status`);
  }

  async regenerate(taskId: string, data: RegenerateRequest) {
    return this.request<RegenerateResponse>(
      `${this.BASE_URL}/${taskId}/regenerate`,
      {
        method: "POST",
        body: data,
      },
    );
  }

  async complete(siteId: string) {
    return this.request<{ message: string }>(`${this.BASE_URL}/complete`, {
      method: "POST",
      body: { site_id: siteId },
    });
  }

  async createProject(data: CreateProjectRequest) {
    return this.request<CreateProjectResponse>(
      `${this.BASE_URL}/create-project`,
      {
        method: "POST",
        body: data,
      },
    );
  }

  async verifyConnection(data: VerifyConnectionRequest) {
    return this.request<VerifyConnectionResponse>(
      `${this.BASE_URL}/verify-connection`,
      {
        method: "POST",
        body: data,
      },
    );
  }
}

export const hostbentoOnboardingApi = new HostbentoOnboardingApi();

interface RequestCodeRequest {
  email: string;
  captcha_token: string;
}

interface RequestCodeResponse {
  message: string;
  code?: string;
  expires_in_minutes: number;
}

interface VerifyCodeRequest {
  email: string;
  code: string;
  captcha_token: string;
  next?: string;
}

interface VerifyCodeResponse {
  message: string;
  redirect_url: string;
}

interface GoogleLoginRequest {
  id_token: string;
  next?: string;
  intent?: string;
}

interface GoogleLoginResponse {
  message: string;
  redirect_url: string;
}

export class HostbentoAuthApi extends ApiService {
  private readonly BASE_URL = "/api/v1/auth";

  async requestCode(data: RequestCodeRequest) {
    return this.request<RequestCodeResponse>(`${this.BASE_URL}/request-code`, {
      method: "POST",
      body: data,
    });
  }

  async verifyCode(data: VerifyCodeRequest) {
    return this.request<VerifyCodeResponse>(`${this.BASE_URL}/verify-code`, {
      method: "POST",
      body: data,
    });
  }

  async googleLogin(data: GoogleLoginRequest) {
    return this.request<GoogleLoginResponse>(`${this.BASE_URL}/google-login`, {
      method: "POST",
      body: data,
    });
  }
}

export const hostbentoAuthApi = new HostbentoAuthApi();
